import { Component, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DecimalPipe } from '@angular/common';
import { FeedbackView, SecretaryJudgeValues } from '../feedback.service';
import { categoryResultsUrl, eventPageUrl } from '../feedback-links';
import { CriteriaLabelsService } from '../criteria-labels.service';

type Criterion = {
  label: string;
  value: number;
};

type JudgeCard = {
  judgeCode: string;
  criteria: Criterion[];
  total: number;
  feedback: string | null;
  collapsed: boolean;
};

type Program = {
  judges: JudgeCard[];
  collapsed: boolean;
  showScores: boolean;
};

type Round = {
  roundName: string;
  programs: Program[];
};

@Component({
  selector: 'dance-feedback-view',
  standalone: true,
  imports: [TranslateModule, DecimalPipe],
  templateUrl: './dance-feedback-view.component.html',
  styleUrls: ['./dance-feedback-view.component.scss'],
})
export class DanceFeedbackViewComponent implements OnInit {
  @Input() view: FeedbackView;

  rounds: Round[] = [];

  constructor(private criteriaLabels: CriteriaLabelsService) {}

  ngOnInit() {
    this.applyData(this.view);
  }

  get eventUrl(): string {
    return eventPageUrl(this.view.eventId);
  }

  get resultsUrl(): string {
    return categoryResultsUrl(this.view.eventId, this.view.categoryId);
  }

  get totalPrograms(): number {
    return this.rounds.reduce((sum, round) => sum + round.programs.length, 0);
  }

  toggleProgram(program: Program) {
    program.collapsed = !program.collapsed;
  }

  toggleJudge(judge: JudgeCard) {
    judge.collapsed = !judge.collapsed;
  }

  anyExpanded(): boolean {
    return this.rounds.some((round) =>
      round.programs.some((program) => !program.collapsed || program.judges.some((judge) => !judge.collapsed))
    );
  }

  toggleAll() {
    const collapse = this.anyExpanded();
    this.rounds.forEach((round) =>
      round.programs.forEach((program) => {
        program.collapsed = collapse;
        program.judges.forEach((judge) => (judge.collapsed = collapse));
      })
    );
  }

  private applyData(view: FeedbackView) {
    this.rounds = view.rounds.map((round) => ({
      roundName: round.roundName,
      programs: round.programs.map((program) => ({
        collapsed: false,
        showScores: program.danceFeedbackMode !== 'TEXT',
        judges: [...program.feedbacks]
          .sort((a, b) => this.compareJudgeCode(a.judgeCode, b.judgeCode))
          .map((feedback) => {
            const values = this.orderedValues(feedback.judgeValues);
            const labels = this.criteriaLabels.labels(
              'dance',
              view.judgingRulesProfileId,
              feedback.judgeCode,
              values.length
            );
            return {
              judgeCode: feedback.judgeCode,
              criteria: values.map((value, index) => ({ label: labels[index], value })),
              total: values.reduce((sum, value) => sum + value, 0),
              feedback: feedback.feedback,
              collapsed: false,
            };
          }),
      })),
    }));
  }

  private orderedValues(judgeValues: SecretaryJudgeValues | null): number[] {
    if (!judgeValues) {
      return [];
    }
    return Object.keys(judgeValues)
      .filter((key) => /^value\d+$/.test(key))
      .sort((a, b) => Number(a.slice('value'.length)) - Number(b.slice('value'.length)))
      .map((key) => judgeValues[key]);
  }

  private compareJudgeCode(a: string, b: string): number {
    const prefixA = a.replace(/\d+$/, '');
    const prefixB = b.replace(/\d+$/, '');
    if (prefixA !== prefixB) {
      return prefixA.localeCompare(prefixB);
    }
    return this.judgeNumber(a) - this.judgeNumber(b);
  }

  private judgeNumber(code: string): number {
    const match = code.match(/\d+$/);
    return match ? Number(match[0]) : 0;
  }
}
